import type { DemandScope } from "~/utils/demandProjection"

// Composite-scope "all" sentinels. NULL can't sit in a composite primary key or be an equality
// upsert/join target, so the demand tables encode "all on this axis" as a sentinel value.
export const CHANNEL_ALL = ""
export const WAREHOUSE_ALL = ""
export const MARKET_ALL = ""

// weekStart sentinel = the horizon-total pin. A real DATE so weekStart can be NOT NULL in
// DemandLevel's primary key; UTC-anchored so it serializes to DATE '0001-01-01' in any timezone.
export const HORIZON_SENTINEL = new Date("0001-01-01T00:00:00.000Z")

// weekStart sentinel = a cell's share pin: `value` is its share of the variant's Manual total, so
// the cell moves with the total and the total never moves (`DemandSharePinIntent`).
export const SHARE_SENTINEL = new Date("0002-01-01T00:00:00.000Z")

// Every variant in the workspace: a collection-wide row is ONE row expanded at read time, so
// variants added later are covered. A sentinel, not NULL, for the same reason as the axes above.
export const VARIANT_ALL = ""

export const isCollectionWide = (row: { variantId: string }) => row.variantId === VARIANT_ALL

// Mirrored as a literal in `shared/sql/calculated/DemandBaselineAggregate.sql`; readers require a
// match and fall back to the SaleBaseData scan until the pipeline rebuilds. BUMP BOTH in one commit
// whenever the window, the gross-realised rule or a selected COLUMN changes.
export const DEMAND_BASELINE_DEFINITION_VERSION = 2

export type DemandGrain = "scoped" | "whole-variant"

export const WHOLE_VARIANT_SCOPE: DemandScope = { channel: null, warehouseId: null, marketId: null }

export type ScopeColumns = { channel: string; warehouseId: string; marketId: string }

/** Set on one combination rather than the whole variant ("" on every axis). */
export const isCombinationScoped = (c: ScopeColumns): boolean =>
  c.channel !== "" || c.warehouseId !== "" || c.marketId !== ""

// In-process identity only, never persisted. `combinations.ts`'s `ruleKey` looks identical but is
// NOT this: it goes into changelog meta, so its format is history. `|`, not a space: ids are
// ingested tenant data and may contain spaces.
export const cellKey = (c: ScopeColumns) => `${c.warehouseId}|${c.channel}|${c.marketId}`

export const cellPairKey = (c: { warehouseId: string; channel: string }) =>
  `${c.warehouseId}|${c.channel}`

// DemandScope (null = "all") -> DB sentinel columns. Single source for every write + proc-arg build.
export function scopeToColumns(scope: DemandScope): ScopeColumns {
  return {
    channel: scope.channel ?? CHANNEL_ALL,
    warehouseId: scope.warehouseId ?? WAREHOUSE_ALL,
    marketId: scope.marketId ?? MARKET_ALL,
  }
}

// DB sentinel columns -> DemandScope (sentinel = null). Single source for the projection feed.
export function columnsToScope(row: ScopeColumns): DemandScope {
  return {
    channel: row.channel === CHANNEL_ALL ? null : row.channel,
    warehouseId: row.warehouseId === WAREHOUSE_ALL ? null : row.warehouseId,
    marketId: row.marketId === MARKET_ALL ? null : row.marketId,
  }
}

const SCOPE_AXES = ["channel", "warehouseId", "marketId"] as const

/** Narrowed on at least one axis. */
export const isScoped = (s: DemandScope): boolean => SCOPE_AXES.some((axis) => s[axis] != null)

// The one market label: every surface that shows a market resolves it here and falls back the same way.
export const marketNameOf = (
  markets: readonly { id: string; name: string }[],
  id: string,
): string => markets.find((m) => m.id === id)?.name ?? id

export const channelNameOf = (
  channels: readonly { id: string; name: string }[] | undefined,
  id: string,
): string => channels?.find((c) => c.id === id)?.name ?? id

export const warehouseNameOf = (
  warehouses: readonly { id: string; name: string }[] | undefined,
  id: string,
): string | undefined => warehouses?.find((w) => w.id === id)?.name

// Which hierarchy level a pin/adjustment lives at.
export function scopeLabel(s: DemandScope): string {
  const parts = [
    s.warehouseId !== null && s.warehouseId,
    s.channel !== null && s.channel,
    s.marketId !== null && s.marketId,
  ].filter(Boolean)
  return parts.length ? parts.join(" · ") : "Variant total (all slices)"
}
